import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';

import { Checkbox, IconButton } from '@/components';

import { IconPicker, renderIconByName } from '../../../modules/IconPicker';
import { ColorPickerAdvanced } from '../controls/ColorPickerAdvanced';
import { NumberInput } from '../controls/NumberInput';
import { StyleToggleButton } from '../controls/StyleToggleButton';
import type { LayoutElementStyle } from '../styleTypes';

interface IconSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
  elementType?: string;
}

export function IconSection({ style, onChange, elementType }: IconSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';

  const defaultSize = elementType === 'heading' ? 20 : elementType === 'section' ? 24 : 16;

  return (
    <div className="space-y-3">
      {/* Icon Picker */}
      <div>
        <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {t(`${p}icon.label`)}
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <IconPicker
              value={style.icon || ''}
              onChange={(icon) => onChange({ icon })}
              ariaLabel={t(`${p}icon.pick`)}
            />
          </div>
          {style.icon && (
            <IconButton
              variant="subtle"
              aria-label={t(`${p}icon.remove`)}
              onClick={() =>
                onChange({
                  icon: undefined,
                  iconPosition: undefined,
                  iconSize: undefined,
                  iconColor: undefined,
                  iconOpacity: undefined,
                  iconRotation: undefined,
                  iconMarginRight: undefined,
                  iconMarginBottom: undefined,
                  iconBackgroundColor: undefined,
                  iconBackgroundRadius: undefined,
                  iconBackgroundPadding: undefined,
                  iconStrokeWidth: undefined,
                })
              }
              className="text-gray-400 hover:text-red-500"
            >
              <Trash2 className="w-4 h-4" />
            </IconButton>
          )}
        </div>
      </div>

      {style.icon && (
        <>
          {/* Preview */}
          <div className="flex justify-center py-3">
            <div
              className="inline-flex items-center justify-center"
              style={
                style.iconBackgroundColor
                  ? {
                      backgroundColor: style.iconBackgroundColor,
                      borderRadius:
                        style.iconBackgroundRadius !== undefined
                          ? `${style.iconBackgroundRadius}px`
                          : '9999px',
                      padding: `${style.iconBackgroundPadding ?? 4}px`,
                      color: style.iconColor,
                    }
                  : { color: style.iconColor }
              }
            >
              {renderIconByName(style.icon, 'h-6 w-6')}
            </div>
          </div>

          {/* Position */}
          <div>
            <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
              {t(`${p}icon.position`)}
            </label>
            <div className="flex gap-1">
              {(
                [
                  { v: 'left', label: t(`${p}common.left`) },
                  { v: 'right', label: t(`${p}common.right`) },
                  { v: 'top', label: t(`${p}common.top`) },
                  { v: 'center', label: t(`${p}common.center`) },
                ] as const
              ).map(({ v, label }) => (
                <StyleToggleButton
                  key={v}
                  active={(style.iconPosition || 'left') === v}
                  onClick={() => onChange({ iconPosition: v })}
                  className="text-[10px]"
                >
                  {label}
                </StyleToggleButton>
              ))}
            </div>
          </div>

          {/* Size + Rotation */}
          <div className="flex flex-wrap gap-2">
            <NumberInput
              value={style.iconSize ?? defaultSize}
              onChange={(v) => onChange({ iconSize: v })}
              min={12}
              max={64}
              label={t(`${p}common.size`)}
              unit="px"
              className="min-w-[7rem] flex-1"
            />
            <NumberInput
              value={style.iconRotation ?? 0}
              onChange={(v) => onChange({ iconRotation: v })}
              min={0}
              max={360}
              label={t(`${p}icon.rotation`)}
              unit="°"
              className="min-w-[7rem] flex-1"
            />
          </div>

          <ColorPickerAdvanced
            color={style.iconColor}
            onChange={(c) => onChange({ iconColor: c })}
            opacity={style.iconOpacity}
            onOpacityChange={(o) => onChange({ iconOpacity: o })}
            label={t(`${p}icon.iconColor`)}
          />

          {/* Spacing */}
          <div className="flex flex-wrap gap-2">
            {style.iconPosition === 'top' ? (
              <NumberInput
                value={style.iconMarginBottom ?? 4}
                onChange={(v) => onChange({ iconMarginBottom: v })}
                min={0}
                max={32}
                label={t(`${p}icon.spacingBottom`)}
                unit="px"
                className="min-w-[7rem] flex-1"
              />
            ) : (
              <NumberInput
                value={style.iconMarginRight ?? 8}
                onChange={(v) => onChange({ iconMarginRight: v })}
                min={0}
                max={32}
                label={t(`${p}icon.spacing`)}
                unit="px"
                className="min-w-[7rem] flex-1"
              />
            )}
            <NumberInput
              value={style.iconStrokeWidth}
              onChange={(v) => onChange({ iconStrokeWidth: v })}
              min={1}
              max={3}
              step={0.5}
              label={t(`${p}icon.strokeWidth`)}
              unit=""
              className="min-w-[7rem] flex-1"
            />
          </div>

          {/* Icon Background */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">
                {t(`${p}icon.background`)}
              </label>
              <Checkbox
                checked={!!style.iconBackgroundColor}
                onChange={(e) =>
                  onChange({
                    iconBackgroundColor: e.target.checked ? '#EFF6FF' : undefined,
                    iconBackgroundRadius: e.target.checked ? 9999 : undefined,
                    iconBackgroundPadding: e.target.checked ? 6 : undefined,
                  })
                }
                label={<span className="text-[10px] text-gray-400">{t(`${p}common.enable`)}</span>}
                className="h-3 w-3"
              />
            </div>
            {style.iconBackgroundColor && (
              <div className="space-y-2 p-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <ColorPickerAdvanced
                  color={style.iconBackgroundColor}
                  onChange={(c) => onChange({ iconBackgroundColor: c || '#EFF6FF' })}
                  label={t(`${p}effects.backgroundColor`)}
                  allowClear={false}
                />
                <div className="flex flex-wrap gap-2">
                  <NumberInput
                    value={style.iconBackgroundRadius ?? 9999}
                    onChange={(v) => onChange({ iconBackgroundRadius: v })}
                    min={0}
                    max={9999}
                    label={t(`${p}common.borderRadius`)}
                    unit="px"
                    className="min-w-[7rem] flex-1"
                  />
                  <NumberInput
                    value={style.iconBackgroundPadding ?? 4}
                    onChange={(v) => onChange({ iconBackgroundPadding: v })}
                    min={0}
                    max={24}
                    label={t(`${p}icon.padding`)}
                    unit="px"
                    className="min-w-[7rem] flex-1"
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
